import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, Gauge, ChevronDown } from 'lucide-react';
import type { Effort } from '@/lib/agents/types';
import { effortLevelsFor } from '@/lib/providers/registry';
import { cn } from '@/lib/utils';

const LABEL: Record<Effort, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  extra_high: 'Extra high',
  max: 'Max',
};

interface Props {
  effort: Effort;
  modelId: string;
  onSelect: (e: Effort) => void;
}

export function EffortMenu({ effort, modelId, onSelect }: Props) {
  const levels = effortLevelsFor(modelId);

  // Provider exposes no reasoning knob (e.g. Gemini CLI) — hide the picker
  // entirely rather than showing a dishonest disabled control.
  if (levels.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="focus-ring flex items-center gap-1 rounded-md px-2 py-1 text-[12.5px] text-fg-muted hover:bg-surface-2 hover:text-fg">
          <Gauge className="h-3.5 w-3.5" />
          <span className="font-medium">{LABEL[effort]}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-[200px] rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
        >
          <DropdownMenu.Label className="px-2 py-1 text-2xs uppercase tracking-wide text-fg-subtle">
            Reasoning effort
          </DropdownMenu.Label>
          {levels.map((value) => (
            <DropdownMenu.Item
              key={value}
              className="menu-item"
              onSelect={() => onSelect(value)}
            >
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  value === effort ? 'text-accent' : 'opacity-0',
                )}
              />
              <span>{LABEL[value]}</span>
            </DropdownMenu.Item>
          ))}

          <DropdownMenu.Separator className="my-1 h-px bg-border" />

          <div className="menu-item" data-disabled aria-disabled>
            <span className="h-3.5 w-3.5" />
            <span className="flex-1">Fast mode</span>
            <span className="text-2xs text-fg-subtle">Off</span>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
